import { configDates } from "@/app/infrastructures/misc/common-library/modules/Date";
import formatDate from "@/app/infrastructures/misc/common-library/FormatDate";
import {
  ConvertObjectCamelToSnakeCase,
  formatDateNormal,
  formatTimeRange
} from "@/app/infrastructures/misc/Utils";
import formatDateBasedAccountTimezone from "@/app/infrastructures/misc/common-library/FormatDateBasedAccountTimezone";

export class PickupCorporateAddress {
  pcaId = 0;
  pcaName = "";
  pcaLatlon = "";
  pcaIsDefault = false;
  pcaPhoneNumber = "";
  pcaAddress = "";
  pcaPicName = "";
  pcaPostalCode = "";
  pcaDistrictId = 0;
  pcaDistrictName = "";

  constructor(fields?: Partial<PickupCorporateAddress>) {
    Object.assign(this, fields);
  }
}

export interface MergedFromShipmentItem {
  shipmentId: string;
  totalKoli: number;
  totalWeight: number;
}

export class ListPickupDetail {
  the3LcPickup = "";
  actualTotalKoli = 0;
  actualTotalShipment = 0;
  actualTotalTonase = 0;
  additionalKoli = 0;
  agentHubTotalShipment = 0;
  agentHubTotalTonase = 0;
  agentHubTotalKoli = 0;
  clientId = "";
  clientParentId = "";
  clientRef = "";
  countryCode = "ID";
  courierId = 0;
  courierName = "";
  courierNote = "";
  courierPhone = "";
  cancelNote = "";
  createdAt = "";
  createdBy = "";
  dropOffProof: string[] = [];
  dhlLabelPrintUrl = "";
  estimateTotalKoli = 0;
  estimateTotalShipment = 0;
  estimateTotalTonase = 0;
  geoloc = "";
  isDhlFirstMilePrintEnabled = false;
  latestStatus = "";
  listStt: ListStt[] = [];
  mergedFromShipmentIds: string[] = [];
  mergedFromShipments: MergedFromShipmentItem[] = [];
  mergedToShipmentId = "";
  origin = "";
  pickupAddress = "";
  pickupDate = "";
  pickupProof: string[] = [];
  pickupTime = "";
  pickupType = "";
  actualPickupDateTime = "";
  plateNo = "";
  serviceCode = "";
  shipmentId = "";
  specialHandling: string[] = [];
  transportType = "";
  updatedAt = "";
  updatedBy = "";
  timezone = "";
  pickupCorporateAddress = new PickupCorporateAddress();

  constructor(fields?: Partial<ListPickupDetail>) {
    Object.assign(this, fields);
  }

  get formattedPickupTime(): string {
    if (!this.pickupTime) {
      return "";
    }
    return [formatTimeRange(this.pickupTime), this.timezoneLabel]
      .filter(Boolean)
      .join(" ");
  }

  get timezoneLabel(): string {
    return configDates(this.timezone)[this.countryCode.toLowerCase()] || "";
  }

  get courierAvailable(): boolean {
    if (this.latestStatus.toUpperCase() === "CRRNFD") {
      return false;
    }
    return Boolean(this.courierId || this.courierName || this.courierPhone);
  }

  get canPrintDhlLabel(): boolean {
    return (
      this.isDhlFirstMilePrintEnabled && Boolean(this.dhlLabelPrintUrl?.trim())
    );
  }

  get summaryPickupDateLabel(): string {
    return this.pickupDate
      ? formatDateNormal(this.pickupDate, "D MMMM YYYY")
      : "";
  }

  get summaryPickupTimeLabel(): string {
    return this.formattedPickupTime;
  }

  get createdAtSidebarLabel(): string {
    if (!this.createdAt) {
      return "";
    }
    return [formatDateNormal(this.createdAt, "D MMMM YYYY, HH.mm"), "MYT"]
      .filter(Boolean)
      .join(" ");
  }

  get agentHubKoliCount(): number {
    return 0;
  }

  get formatCreatedAt() {
    return formatDate(this.createdAt);
  }

  get formatUpdatedAt() {
    return formatDate(this.updatedAt);
  }

  get formattedActualPickupDateTime(): string {
    return formatDateBasedAccountTimezone(this.actualPickupDateTime);
  }

  get getPlatNo(): string {
    return this.latestStatus.toUpperCase() === "CRRNFD" ? "-" : this.plateNo;
  }

  get lastStatusGenesis(): string {
    const statuses: Record<string, string> = {
      CRRNFD: "Mencari Kurir",
      CRROTW: "Kurir Ditugaskan",
      CRRPIC: "Paket di Pick Up",
      CRRDON: "Selesai",
      CRRCNC: "Dibatalkan Kurir",
      CSTCNC: "Dibatalkan Klien",
      ADMCNC: "Dibatalkan Admin"
    };
    const status = this.latestStatus.toUpperCase();
    return statuses[status] || status;
  }

  get lastStatusColor(): string {
    const colors: Record<string, string> = {
      CRRNFD: "yellow",
      CRROTW: "aqua",
      CRRPIC: "blue",
      CRRDON: "green",
      CRRCNC: "red",
      CSTCNC: "red",
      ADMCNC: "red"
    };
    return colors[this.latestStatus.toUpperCase()] || "gray";
  }

  get pickupInProgress(): boolean {
    return ["CRROTW", "CRRPIC"].includes(this.latestStatus.toUpperCase());
  }

  get isCancelled(): boolean {
    return ["CRRCNC", "CSTCNC", "ADMCNC"].includes(
      this.latestStatus.toUpperCase()
    );
  }

  get isCancelable(): boolean {
    if (!this.latestStatus || this.isCancelled) {
      return false;
    }
    return !["CRROTW", "CRRPIC", "CRRDON", "SHPMRG", "SHPDON"].includes(
      this.latestStatus.toUpperCase()
    );
  }

  get shouldCourierDataShown(): boolean {
    return ["CRROTW", "CRRPIC", "CRRDON"].includes(
      this.latestStatus.toUpperCase()
    );
  }

  get shouldPickupProofImageShown(): boolean {
    return ["CRRPIC", "CRRDON"].includes(this.latestStatus.toUpperCase());
  }

  get isShipmentDone(): boolean {
    return this.latestStatus.toUpperCase() === "CRRDON";
  }

  get isPcaEmpty(): boolean {
    return !this.pickupCorporateAddress?.pcaId;
  }
}

export class ListStt {
  bookingDate = "";
  grossWeight = 0;
  serviceCode = "";
  sttNo = "";
  totalKoli = 0;
  dhlLabelPrintUrl = "";

  constructor(fields?: Partial<ListStt>) {
    Object.assign(this, fields);
  }

  get formatedBookingDate() {
    return formatDate(this.bookingDate);
  }
}

export class CancelPickupApiRequest {
  shipmentId = "";
  cancelNote = "";

  constructor(fields?: Partial<CancelPickupApiRequest>) {
    Object.assign(this, fields);
  }

  toJSON(): string {
    return JSON.stringify([ConvertObjectCamelToSnakeCase({ ...this })]);
  }
}

export class CreatePickupSuccessResponse {
  shipmentId = "";
  isDhlFirstMile = false;
  labelUrl = "";
  dhlReferenceNo = "";
  dhlCreateDegraded = false;

  constructor(fields?: Partial<CreatePickupSuccessResponse>) {
    Object.assign(this, fields);
  }
}
